import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, X, ArrowRight } from 'lucide-react';
import { pasqualinoStory } from '../content/pasqualinoStory';
import { FEATURED, optimizeImg } from '../content/mediaLibrary';
import useSEO from '../hooks/useSEO';

const PASQUALINO_VIDEO = "https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/2m9usk5b_PASC%20-.mp4";
const FRANCESCO_VIDEO = "https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/9ngpa9vp_PIZZI.mp4";

// Per-team-member portraits, keyed by the translation key.
// `type` is 'video' or 'image'. If no entry exists, the card falls back
// to the initial-letter placeholder.
const TEAM_MEMBER_MEDIA = {
  cherry: {
    type: 'video',
    src: 'https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/b9yxlgoa_CHERRY.mp4',
    poster: FEATURED.cherryPoster,
  },
  lenny: {
    type: 'image',
    src: 'https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/shdr725w_Screenshot%202026-04-24%20144359.png',
    objectPosition: 'center top',
  },
  sara: {
    type: 'image',
    src: 'https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/utx4cq51_Screenshot%202026-04-24%20144829.png',
    objectPosition: 'center 20%',
  },
  marco: {
    type: 'image',
    src: 'https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/9veddx77_20250419_121644_DSC05312%281%29.JPG',
    objectPosition: 'center 30%',
  },
};

// (optimizeImg is imported from '../content/mediaLibrary')

// Content video for team page background
const TEAM_VIDEO = "https://customer-assets.emergentagent.com/job_italian-grooming/artifacts/38upf73m_7.mp4";

const TeamPage = () => {
  const { language, t } = useLanguage();
  useSEO({ title: t.seo.team.title, description: t.seo.team.description });
  const [showFullStory, setShowFullStory] = useState(false);
  
  const story = pasqualinoStory[language];
  const teamMembers = Object.entries(t.team.teamMembers);

  return (
    <div className="min-h-screen pt-20" data-testid="team-page">
      {/* Hero Header */}
      <section className="py-16 lg:py-24 bg-[#17201a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="deco-line" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#6e756a]">{t.nav.team}</span>
            </div>
            <h1 className="font-serif text-5xl lg:text-7xl font-light tracking-tighter text-[#f5f0e8] mb-6" data-testid="team-title">
              {t.team.title}
            </h1>
            <p className="text-lg text-[#6e756a] font-light max-w-xl">
              {t.team.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pasqualino - Full Width Feature */}
      <section className="bg-[#f5f0e8]">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Portrait video — muted autoplay loop */}
          <motion.div 
            initial={{ opacity: 0, scale: 1.05 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative aspect-[4/5] lg:aspect-auto lg:min-h-[90vh] overflow-hidden bg-[#1A1A18]"
            data-testid="team-pasqualino"
          >
            <video
              src={PASQUALINO_VIDEO}
              poster={optimizeImg(FEATURED.pasqualinoPoster, 1200, 80)}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-label={`${story.name} — ${t.team.pasqualino.role}, Casanova Salon Barbier`}
              className="w-full h-full object-cover object-top"
              data-testid="team-pasqualino-video"
            />
            {/* Subtle overlay for text readability on mobile */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#17201a]/60 via-transparent to-transparent lg:hidden" />
          </motion.div>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col justify-center p-8 lg:p-16 xl:p-24"
          >
            <span className="text-xs uppercase tracking-[0.25em] text-[#a84b2a] mb-4 font-medium">
              {story.role}
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-[#17201a] mb-4 tracking-tight leading-[1.1]">
              {story.name}
            </h2>
            <p className="text-lg text-[#6e756a] font-light mb-8 italic">
              {story.tagline}
            </p>
            
            <p className="text-[#17201a]/80 font-light leading-relaxed mb-8 text-lg">
              {story.intro}
            </p>

            {/* Quote */}
            <blockquote className="border-l-2 border-[#a84b2a] pl-6 mb-8">
              <p className="font-serif text-xl lg:text-2xl text-[#17201a] italic leading-relaxed">
                "{story.quote}"
              </p>
            </blockquote>

            {/* Certifications */}
            <div className="mb-8">
              <h4 className="text-xs uppercase tracking-[0.2em] text-[#6e756a] mb-4 flex items-center gap-2">
                <Award size={14} className="text-[#a84b2a]" />
                {t.team.certifications}
              </h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {story.certifications.map((cert) => (
                  <li key={cert} className="text-sm text-[#17201a]/70 font-light flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#a84b2a] rounded-full flex-shrink-0" />
                    {cert}
                  </li>
                ))}
              </ul>
            </div>

            {/* Read Full Story Button */}
            <button
              onClick={() => setShowFullStory(true)}
              className="btn-cta self-start inline-flex items-center gap-3"
              data-testid="read-full-story"
            >
              {language === 'fr' ? 'Lire mon histoire' : 'Read my story'}
              <ArrowRight size={16} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Full Story Modal */}
      <AnimatePresence>
        {showFullStory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#17201a]/95 overflow-y-auto"
            onClick={() => setShowFullStory(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.4 }}
              className="min-h-screen py-12 px-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-w-4xl mx-auto bg-[#f5f0e8] relative">
                {/* Close button */}
                <button
                  onClick={() => setShowFullStory(false)}
                  className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center border border-[#6e756a]/30 hover:border-[#a84b2a] hover:bg-[#a84b2a] hover:text-[#f5f0e8] transition-all z-10"
                  data-testid="close-story-modal"
                >
                  <X size={20} />
                </button>

                {/* Hero Video */}
                <motion.div 
                  initial={{ opacity: 0, scale: 1.05 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="relative aspect-[16/9] overflow-hidden bg-[#17201a]"
                >
                  <video
                    src={PASQUALINO_VIDEO}
                    poster={optimizeImg(FEATURED.pasqualinoPoster, 1200, 80)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient overlay for text */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#17201a] via-transparent to-transparent" />
                  
                  {/* Name overlay on video */}
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="absolute bottom-8 left-8 right-8"
                  >
                    <span className="text-xs uppercase tracking-[0.25em] text-[#a84b2a] mb-2 block">
                      {story.role}
                    </span>
                    <h2 className="font-serif text-4xl lg:text-6xl text-[#f5f0e8] tracking-tight">
                      {story.name}
                    </h2>
                  </motion.div>
                </motion.div>

                {/* Content */}
                <div className="p-8 lg:p-12">
                  {/* Intro */}
                  <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="font-serif text-xl lg:text-2xl text-[#17201a] italic leading-relaxed mb-12 border-l-2 border-[#a84b2a] pl-6"
                  >
                    {story.intro}
                  </motion.p>

                  <div className="space-y-10">
                    {story.sections.map((section, index) => (
                      <motion.div 
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 + (index * 0.1) }}
                      >
                        <h3 className="font-serif text-2xl lg:text-3xl text-[#17201a] mb-4">
                          {section.title}
                        </h3>
                        <div className="text-[#17201a]/75 font-light leading-relaxed whitespace-pre-line">
                          {section.content}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Bottom Quote */}
                  <motion.blockquote 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1 }}
                    className="mt-16 pt-8 border-t border-[#6e756a]/20"
                  >
                    <p className="font-serif text-2xl lg:text-3xl text-[#17201a] italic text-center leading-relaxed">
                      "{story.quote}"
                    </p>
                    <cite className="block text-center mt-6 text-sm text-[#6e756a] not-italic uppercase tracking-wider">
                      — {story.name}
                    </cite>
                  </motion.blockquote>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Philosophy Quote */}
      <section className="py-20 lg:py-28 bg-[#17201a]" data-testid="team-philosophy">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.blockquote
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-serif text-2xl lg:text-4xl font-light text-[#f5f0e8] italic leading-relaxed"
          >
            {t.team.philosophy}
          </motion.blockquote>
          <div className="mt-8 flex justify-center">
            <div className="deco-line" />
          </div>
        </div>
      </section>

      {/* Francesco */}
      <section className="py-16 lg:py-24 bg-[#f5f0e8]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
            data-testid="team-francesco"
          >
            <div className="aspect-[3/4] relative overflow-hidden bg-[#1A1A18]">
              <video
                src={FRANCESCO_VIDEO}
                poster={optimizeImg(FEATURED.francescoPoster, 1000, 80)}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={`${t.team.francesco.name} — ${t.team.cofounder}, Casanova Salon Barbier`}
                className="w-full h-full object-cover"
                data-testid="team-francesco-video"
              />
            </div>
            
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#a84b2a] mb-4 block font-medium">
                {t.team.cofounder}
              </span>
              <h2 className="font-serif text-3xl lg:text-4xl text-[#17201a] mb-6 tracking-tight">
                {t.team.francesco.name}
              </h2>
              <p className="text-[#6e756a] font-light leading-relaxed text-lg">
                {t.team.francesco.bio}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-16 lg:py-24 bg-[#17201a]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-4 mb-12">
            <div className="deco-line" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#6e756a]">{t.team.title}</span>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {teamMembers.map(([key, member], index) => {
              const media = TEAM_MEMBER_MEDIA[key];
              const ariaLabel = `${member.name} — ${member.role}, Casanova Salon Barbier`;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`group ${index % 2 === 1 ? 'lg:mt-12' : ''}`}
                  data-testid={`team-${key}`}
                >
                  <div className="aspect-[3/4] bg-[#3d5446] mb-4 flex items-center justify-center overflow-hidden relative">
                    {media?.type === 'video' ? (
                      <video
                        src={media.src}
                        poster={media.poster ? optimizeImg(media.poster, 800, 80) : undefined}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        aria-label={ariaLabel}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        data-testid={`team-${key}-video`}
                      />
                    ) : media?.type === 'image' ? (
                      <img
                        src={optimizeImg(media.src)}
                        alt={ariaLabel}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        style={{ objectPosition: media.objectPosition || 'center top' }}
                        data-testid={`team-${key}-image`}
                      />
                    ) : (
                      <span className="font-serif text-6xl text-[#f5f0e8]/20 group-hover:scale-110 transition-transform duration-500">
                        {member.name.charAt(0)}
                      </span>
                    )}
                  </div>
                  <h4 className="font-serif text-xl text-[#f5f0e8] mb-1">{member.name}</h4>
                  <p className="text-xs uppercase tracking-wider text-[#a84b2a] mb-2">{member.role}</p>
                  <p className="text-xs text-[#6e756a] font-light">{member.specialty}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
